import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell,
} from "recharts";
import { useLang } from "@/context/LanguageContext";
import { formatEUR } from "@/lib/i18n";
import AltmanCard from "@/components/wizard/AltmanCard";
import TrendChart from "@/components/wizard/TrendChart";

const STATUS_STYLE = {
  good: { dot: "bg-green-500", pill: "bg-green-50 text-green-700 border-green-200" },
  warn: { dot: "bg-amber-500", pill: "bg-amber-50 text-amber-800 border-amber-200" },
  bad: { dot: "bg-red-500", pill: "bg-red-50 text-red-700 border-red-200" },
  na: { dot: "bg-slate-300", pill: "bg-slate-50 text-slate-500 border-slate-200" },
};

const GROUP_ORDER = ["redditivita", "liquidita", "solidita", "rotazione"];

function RatioCard({ r, lang }) {
  const st = STATUS_STYLE[r.status] || STATUS_STYLE.na;
  const val = r.value === null || r.value === undefined ? "N/A" : `${r.value}${r.unit === "%" ? "%" : ""}`;
  const hasDelta = r.delta !== undefined && r.delta !== null;
  const deltaColor = hasDelta && r.delta > 0 ? "text-green-700" : hasDelta && r.delta < 0 ? "text-red-700" : "text-slate-500";
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 transition-colors hover:border-blue-300">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs font-medium uppercase tracking-wider text-slate-500">
            {lang === "it" ? r.name_it : r.name_en}
          </div>
          <div className="mt-2 font-mono text-2xl font-medium text-slate-900">{val}</div>
          {hasDelta && (
            <div className={`mt-1 text-[11px] font-mono ${deltaColor}`}>
              {r.delta > 0 ? "+" : ""}{r.delta}{r.unit === "%" ? "pp" : ""} vs N-1
            </div>
          )}
        </div>
        <span className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-medium ${st.pill}`}>
          <span className={`h-1.5 w-1.5 rounded-full ${st.dot}`} />
          {r.status.toUpperCase()}
        </span>
      </div>
      <div className="mt-3 text-[11px] leading-relaxed text-slate-500">
        {lang === "it" ? r.formula_it : r.formula_en}
      </div>
    </div>
  );
}

export default function RatiosDashboard({ ratios }) {
  const { t, lang } = useLang();
  const groups = GROUP_ORDER.map((g) => ({
    key: g,
    label: t(`group_${g}`),
    items: ratios.ratios.filter((r) => r.group === g),
  }));

  // Totals chart data
  const totals = ratios.totals;
  const chartData = [
    { name: lang === "it" ? "Immobilizz." : "Fixed", value: totals.immobilizzazioni, fill: "#1D4ED8" },
    { name: lang === "it" ? "Attivo circ." : "Current", value: totals.attivo_circolante, fill: "#60A5FA" },
    { name: lang === "it" ? "Patr. netto" : "Equity", value: totals.patrimonio_netto, fill: "#15803D" },
    { name: lang === "it" ? "Debiti" : "Debt", value: totals.debiti_totali, fill: "#B91C1C" },
  ];
  const ceData = [
    { name: lang === "it" ? "Val. produzione" : "Production", value: totals.valore_produzione, fill: "#1D4ED8" },
    { name: lang === "it" ? "Costi produzione" : "Costs", value: totals.costi_produzione, fill: "#94A3B8" },
    { name: lang === "it" ? "Utile" : "Profit", value: totals.utile_esercizio, fill: "#15803D" },
  ];

  const deltas = ratios.deltas || {};

  return (
    <div className="space-y-8">
      {ratios.altman && <AltmanCard altman={ratios.altman} />}
      {ratios.history && ratios.history.length >= 2 && <TrendChart history={ratios.history} />}
      {/* KPI Totals */}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {[
          { key: "totale_attivo", label: lang === "it" ? "Totale Attivo" : "Total Assets", value: totals.totale_attivo },
          { key: "patrimonio_netto", label: lang === "it" ? "Patrimonio Netto" : "Equity", value: totals.patrimonio_netto },
          { key: "ricavi", label: lang === "it" ? "Ricavi" : "Revenue", value: totals.ricavi || totals.valore_produzione },
          { key: "utile_esercizio", label: lang === "it" ? "Utile d'esercizio" : "Net Income", value: totals.utile_esercizio, highlight: true },
        ].map((k) => {
          const d = deltas[k.key];
          const dcolor = d && d.delta > 0 ? "text-green-700" : d && d.delta < 0 ? "text-red-700" : "text-slate-500";
          return (
            <div key={k.label} className="rounded-xl border border-slate-200 bg-white p-5">
              <div className="text-xs font-medium uppercase tracking-wider text-slate-500">{k.label}</div>
              <div className={`mt-2 font-mono text-xl font-medium ${k.highlight ? "text-blue-700" : "text-slate-900"}`}>
                {formatEUR(k.value, lang)}
              </div>
              {d && d.delta_pct !== null && (
                <div className={`mt-1 text-[11px] font-mono ${dcolor}`}>
                  {d.delta_pct > 0 ? "+" : ""}{d.delta_pct}% vs N-1 · {formatEUR(d.prior, lang)}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Charts */}
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="mb-4 font-heading text-sm font-medium tracking-tight text-slate-900">
            {lang === "it" ? "Composizione patrimoniale" : "Balance sheet composition"}
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartData}>
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#475569" }} tickLine={false} axisLine={{ stroke: "#E2E8F0" }} />
              <YAxis tick={{ fontSize: 11, fill: "#475569" }} tickLine={false} axisLine={{ stroke: "#E2E8F0" }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip
                contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }}
                formatter={(v) => formatEUR(v, lang)}
              />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                {chartData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="mb-4 font-heading text-sm font-medium tracking-tight text-slate-900">
            {lang === "it" ? "Conto economico sintetico" : "Income statement summary"}
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={ceData}>
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#475569" }} tickLine={false} axisLine={{ stroke: "#E2E8F0" }} />
              <YAxis tick={{ fontSize: 11, fill: "#475569" }} tickLine={false} axisLine={{ stroke: "#E2E8F0" }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} formatter={(v) => formatEUR(v, lang)} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                {ceData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Ratios grouped */}
      {groups.map((g) => (
        g.items.length ? (
          <div key={g.key}>
            <div className="mb-3 flex items-center gap-3">
              <div className="text-xs font-medium uppercase tracking-widest text-blue-700">{g.label}</div>
              <div className="h-px flex-1 bg-slate-200" />
            </div>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              {g.items.map((r) => (
                <RatioCard key={r.key} r={r} lang={lang} />
              ))}
            </div>
          </div>
        ) : null
      ))}
    </div>
  );
}
